import { createClient } from "@/app/utils/supabase/client";
import { Batch, BatchInsert, BatchUpdate, EntityShortFormat, FollowUpEntityType } from "@/app/lib/supabase/types";
import { SUPABASE_TABLES } from "../supabase/supabaseDBConstants";

export const batchesApi = {
  async validateBatchUnits(orderId: string, units: number, excludeBatchId?: string): Promise<{ isValid: boolean; availableUnits: number; errorMessage?: string }> {
    const supabase = createClient();
    
    // Get the order details
    const { data: order, error: orderError } = await supabase
      .from(SUPABASE_TABLES.ORDERS_WITH_RELATIONS)
      .select("number_of_units")
      .eq("id", orderId)
      .single();

    if (orderError || !order) {
      return { isValid: false, availableUnits: 0, errorMessage: "Order not found" };
    }

    // Get all batches for this order
    const { data: batches, error: batchesError } = await supabase
      .from(SUPABASE_TABLES.BATCHES)
      .select("id, number_of_units")
      .eq("order_id", orderId)
      .is("deleted_at", null);

    if (batchesError) {
      return { isValid: false, availableUnits: 0, errorMessage: "Failed to fetch batches" };
    }

    // Calculate total units already allocated
    const usedUnits = (batches || [])
      .filter(batch => batch.id !== excludeBatchId)
      .reduce((sum, batch) => sum + (batch.number_of_units || 0), 0);

    const availableUnits = order.number_of_units - usedUnits;

    if (units > availableUnits) {
      return { 
        isValid: false, 
        availableUnits, 
        errorMessage: `Cannot allocate ${units} units. Only ${availableUnits} units available out of ${order.number_of_units} total.` 
      };
    }

    if (units <= 0) {
      return { 
        isValid: false, 
        availableUnits, 
        errorMessage: "Number of units must be greater than 0" 
      };
    }

    return { isValid: true, availableUnits };
  },

  
  async getAllBatches(companyId: string): Promise<Batch[]> {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.BATCHES)
      .select("*")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching all batches:", error);
      throw error;
    }

    return data || [];
  },
  async getBatches(projectId: string): Promise<Batch[]> {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.BATCHES)
      .select("*")
      .eq("project_id", projectId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching batches:", error);
      throw error;
    }

    return data || [];
  },

  async getBatchesByOrder(orderId: string): Promise<Batch[]> {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from("batches")
      .select("*")
      .eq("order_id", orderId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching batches by order:", error);
      throw error;
    }

    return data || [];
  },

  async getBatch(batchId: string): Promise<Batch> {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.BATCHES)
      .select("*")
      .eq("id", batchId)
      .is("deleted_at", null)
      .single();

    if (error) {
      console.error("Error fetching batch:", error);
      throw error;
    }

    if (!data) {
      throw new Error("Batch not found");
    }

    return data;
  },

  async createBatch(batch: BatchInsert): Promise<Batch> {
    const supabase = createClient();
    
    // Validate units before creating
    if (batch.order_id && batch.number_of_units) {
      const validation = await this.validateBatchUnits(batch.order_id, batch.number_of_units);
      if (!validation.isValid) {
        throw new Error(validation.errorMessage || "Invalid batch units");
      }
    }
    
    const { data, error } = await supabase
      .from("batches")
      .insert(batch)
      .select()
      .single();

    if (error) {
      console.error("Error creating batch:", error);
      throw error;
    }

    return data;
  },

  async updateBatch(batchId: string, updates: BatchUpdate): Promise<Batch> {
    const supabase = createClient();
    
    // If updating units, validate first
    if (updates.number_of_units !== undefined) {
      // Get the batch's order_id if not provided
      let orderId = updates.order_id;
      if (!orderId) {
        const { data: existingBatch } = await supabase
          .from(SUPABASE_TABLES.BATCHES)
          .select("order_id")
          .eq("id", batchId)
          .single();
        orderId = existingBatch?.order_id;
      }

      if (orderId) {
        const validation = await this.validateBatchUnits(orderId, updates.number_of_units, batchId);
        if (!validation.isValid) {
          throw new Error(validation.errorMessage || "Invalid batch units");
        }
      }
    }
    
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.BATCHES)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", batchId)
      .select()
      .single();

    if (error) {
      console.error("Error updating batch:", error);
      throw error;
    }

    return data;
  },

  async deleteBatch(batchId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.BATCHES)
      .update({
        deleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", batchId);

    if (error) {
      console.error("Error deleting batch:", error);
      throw error;
    }
  },

  async getBatchesShortFormat(companyId: string): Promise<EntityShortFormat[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.BATCHES)
      .select("id, name, status, number_of_units, created_at, projects(name)")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching batches short format:", error);
      throw error;
    }

    console.log("batches short format", data);

    return (data || []).map((batch) => ({
      id: batch.id,
      name: batch.name + " - " + ((batch.projects as unknown as {name: string})?.name || ""),
      description: `${batch.number_of_units} units`,
      status: batch.status,
      date: batch.created_at,
      entityType: FollowUpEntityType.BATCH,
    }));
  },
};